import { z } from 'zod';
import {
  AbilitySchema,
  ClassSchema,
  EnemySchema,
  GameConfigSchema,
  ItemSchema,
  RaritySchema,
  TalentSchema,
  ZoneSchema,
  type AbilityDef,
  type ClassDef,
  type EnemyDef,
  type GameConfig,
  type ItemDef,
  type RarityDef,
  type TalentDef,
  type ZoneDef,
  UpgradeSchema,
  type UpgradeDef,
  RaceSchema,
  type RaceDef,
  RelicSchema,
  type RelicDef,
  AchievementSchema,
  type AchievementDef,
  BannerSchema,
  type BannerDef,
} from './schema';

export interface Content {
  game: GameConfig;
  classes: Record<string, ClassDef>;
  abilities: Record<string, AbilityDef>;
  talents: Record<string, TalentDef>;
  enemies: Record<string, EnemyDef>;
  items: Record<string, ItemDef>;
  rarities: Record<string, RarityDef>;
  /** Rasy bohatera, w kolejności z pliku (pierwsza = domyślna). */
  races: Record<string, RaceDef>;
  /** Drzewko stałych ulepszeń w obozie, w kolejności z pliku. */
  upgrades: Record<string, UpgradeDef>;
  /** Przedmioty wyprawy (pierścienie, amulety, talizmany) — tylko na czas jednej wyprawy. */
  relics: Record<string, RelicDef>;
  /** Strefy wypraw w kolejności z pliku (od najłatwiejszej). */
  zones: Record<string, ZoneDef>;
  /** Osiągnięcia w kolejności z pliku. */
  achievements: Record<string, AchievementDef>;
  /** Banery przywołania; pierwszy to stały. */
  banners: Record<string, BannerDef>;
  /** Rzadkości od najczęstszej do najrzadszej (kolejność z pliku). */
  rarityOrder: string[];
}

/** Surowe dane prosto z plików JSON, przed walidacją. */
export interface RawContent {
  game: unknown;
  classes: unknown;
  abilities: unknown;
  talents: unknown;
  enemies: unknown;
  items: unknown;
  rarities: unknown;
  zones: unknown;
  upgrades: unknown;
  races: unknown;
  relics?: unknown;
  achievements?: unknown;
  banners?: unknown;
}

export class ContentError extends Error {
  constructor(readonly problems: string[]) {
    super(`Błędy w danych gry:\n- ${problems.join('\n- ')}`);
  }
}

/** Waliduje dane schematami i sprawdza powiązania między nimi (np. czy talent wskazuje istniejącą umiejętność). */
export function loadContent(raw: RawContent): Content {
  const problems: string[] = [];

  const parse = <T>(label: string, schema: z.ZodType<T, z.ZodTypeDef, unknown>, data: unknown): T | undefined => {
    const result = schema.safeParse(data);
    if (result.success) return result.data;
    for (const issue of result.error.issues) {
      problems.push(`${label}${issue.path.length ? '.' + issue.path.join('.') : ''}: ${issue.message}`);
    }
    return undefined;
  };

  const byId = <T extends { id: string }>(label: string, list: T[] | undefined): Record<string, T> => {
    const out: Record<string, T> = {};
    for (const item of list ?? []) {
      if (out[item.id]) problems.push(`${label}: zduplikowane id "${item.id}"`);
      out[item.id] = item;
    }
    return out;
  };

  const game = parse('game', GameConfigSchema, raw.game);
  const classes = byId('classes', parse('classes', z.array(ClassSchema), raw.classes));
  const abilities = byId('abilities', parse('abilities', z.array(AbilitySchema), raw.abilities));
  const talents = byId('talents', parse('talents', z.array(TalentSchema), raw.talents));
  const enemies = byId('enemies', parse('enemies', z.array(EnemySchema), raw.enemies));
  const items = byId('items', parse('items', z.array(ItemSchema), raw.items));
  const rarities = byId('rarities', parse('rarities', z.array(RaritySchema), raw.rarities));
  const zones = byId('zones', parse('zones', z.array(ZoneSchema), raw.zones));
  const upgrades = byId('upgrades', parse('upgrades', z.array(UpgradeSchema), raw.upgrades));
  const races = byId('races', parse('races', z.array(RaceSchema), raw.races));
  const relics = byId('relics', parse('relics', z.array(RelicSchema), raw.relics ?? []));
  const achievements = byId('achievements', parse('achievements', z.array(AchievementSchema), raw.achievements ?? []));
  const banners = byId('banners', parse('banners', z.array(BannerSchema), raw.banners ?? [{ id: 'staly', name: 'Przywołanie', description: '' }]));
  for (const r of Object.values(relics)) {
    if (!rarities[r.rarity]) problems.push(`przedmiot wyprawy "${r.id}": nieznana rzadkość "${r.rarity}"`);
  }
  if (Object.keys(races).length === 0) problems.push('brak ras');
  for (const zone of Object.values(zones)) {
    for (const e of zone.enemies) {
      if (!enemies[e.enemy]) problems.push(`strefa "${zone.id}": nieznany wróg "${e.enemy}"`);
    }
    for (const b of zone.bosses) {
      const def = enemies[b.enemy];
      if (!def) problems.push(`strefa "${zone.id}": nieznany boss "${b.enemy}"`);
      else if (def.behavior !== 'boss') problems.push(`strefa "${zone.id}": "${b.enemy}" nie ma behavior: boss`);
    }
    if (zone.bosses.filter((b) => b.final).length > 1) problems.push(`strefa "${zone.id}": więcej niż jeden boss końcowy`);
    if (!zone.enemies.some((e) => enemies[e.enemy]?.appearsAt === 0)) problems.push(`strefa "${zone.id}": żaden wróg nie pojawia się od 0 sekundy`);
  }
  if (Object.keys(zones).length === 0) problems.push('brak stref wypraw');

  for (const e of Object.values(enemies)) {
    if (e.behavior !== 'chase' && !e.attack) problems.push(`wróg "${e.id}": zachowanie "${e.behavior}" wymaga pola attack`);
    const summon = e.boss?.enrage?.summon;
    if (summon && !enemies[summon.enemy]) problems.push(`boss "${e.id}": nieznany przywoływany wróg "${summon.enemy}"`);
    if (e.boss && e.behavior !== 'boss') problems.push(`wróg "${e.id}": mechaniki bossa wymagają behavior: boss`);
  }
  for (const cls of Object.values(classes)) {
    for (const ability of cls.startingAbilities) {
      if (!abilities[ability]) problems.push(`klasa "${cls.id}": nieznana umiejętność "${ability}"`);
    }
    for (const item of cls.startingItems) {
      if (!items[item]) problems.push(`klasa "${cls.id}": nieznany przedmiot "${item}"`);
    }
  }
  for (const a of Object.values(abilities)) {
    if (!a.evolution) continue;
    const target = abilities[a.evolution];
    if (!target) problems.push(`umiejętność "${a.id}": nieznana ewolucja "${a.evolution}"`);
    else if (!target.evolved) problems.push(`umiejętność "${a.id}": ewolucja "${a.evolution}" musi mieć evolved: true`);
    else if (target.kind !== a.kind) problems.push(`umiejętność "${a.id}": ewolucja musi mieć ten sam rodzaj (kind)`);
    if (a.evolved) problems.push(`umiejętność "${a.id}": forma po ewolucji nie ewoluuje dalej`);
  }
  for (const t of Object.values(talents)) {
    for (const c of t.classes) {
      if (!classes[c]) problems.push(`talent "${t.id}": nieznana klasa "${c}"`);
    }
    if (t.grantsAbility && !abilities[t.grantsAbility]) {
      problems.push(`talent "${t.id}": nieznana umiejętność "${t.grantsAbility}"`);
    }
    for (const req of t.requires) {
      const required = talents[req.talent];
      if (!required) problems.push(`talent "${t.id}": wymaga nieznanego talentu "${req.talent}"`);
      else if (req.rank > required.maxRank) problems.push(`talent "${t.id}": wymaga rangi ${req.rank} talentu "${req.talent}", a ten ma max ${required.maxRank}`);
      if (req.talent === t.id) problems.push(`talent "${t.id}": wymaga sam siebie`);
    }
  }
  for (const item of Object.values(items)) {
    if (item.twoHanded && item.slot !== 'weapon') problems.push(`przedmiot "${item.id}": tylko broń może być dwuręczna`);
    for (const c of item.classes) {
      if (!classes[c]) problems.push(`przedmiot "${item.id}": nieznana klasa "${c}"`);
    }
  }
  for (const cls of Object.values(classes)) {
    for (const itemId of cls.startingItems) {
      if (items[itemId] && !items[itemId].classes.includes(cls.id)) problems.push(`klasa "${cls.id}": przedmiot startowy "${itemId}" nie jest dla tej klasy`);
    }
    if (!Object.values(items).some((i) => i.classes.includes(cls.id))) problems.push(`klasa "${cls.id}": brak przedmiotów do wylosowania`);
  }
  if (Object.keys(classes).length === 0) problems.push('brak klas postaci');
  if (Object.keys(rarities).length === 0) problems.push('brak rzadkości przedmiotów');
  if (!Object.values(enemies).some((e) => e.appearsAt === 0)) problems.push('żaden wróg nie pojawia się od 0 sekundy');

  if (problems.length > 0 || !game) throw new ContentError(problems);
  for (const u of Object.values(upgrades)) {
    if (u.after && !upgrades[u.after]) problems.push(`ulepszenie "${u.id}": nieznany poprzednik "${u.after}"`);
    else if (u.after && upgrades[u.after]!.branch !== u.branch) problems.push(`ulepszenie "${u.id}": poprzednik z innej gałęzi`);
    if (u.branch === 'klasa' && !u.classes?.length) problems.push(`ulepszenie "${u.id}": gałąź „klasa” wymaga pola classes`);
    for (const c of u.classes ?? []) if (!classes[c]) problems.push(`ulepszenie "${u.id}": nieznana klasa "${c}"`);
    if (u.after && upgrades[u.after]?.classes && u.classes?.some((c) => !upgrades[u.after!]!.classes!.includes(c))) {
      problems.push(`ulepszenie "${u.id}": poprzednik jest dla innej klasy`);
    }
    // Węzły różnych klas mogą zajmować to samo miejsce (widać tylko węzły bieżącej klasy).
    const shareClass = (o: UpgradeDef) => !u.classes || !o.classes || o.classes.some((c) => u.classes!.includes(c));
    const clash = Object.values(upgrades).find((o) => o !== u && o.branch === u.branch && o.row === u.row && o.col === u.col && shareClass(o));
    if (clash) problems.push(`ulepszenie "${u.id}": to samo miejsce w drzewku co "${clash.id}"`);
    if (u.modifiers.some((m) => m.tags?.length)) problems.push(`ulepszenie "${u.id}": modyfikatory bez tagów (działa na całą postać)`);
  }
  for (const a of Object.values(achievements)) {
    const c = a.condition;
    if (c.type === 'boss' && enemies[c.enemy]?.behavior !== 'boss') problems.push(`osiągnięcie "${a.id}": "${c.enemy}" nie jest bossem`);
    if ((c.type === 'survive' && c.zone && !zones[c.zone]) || (c.type === 'clear' && !zones[c.zone])) problems.push(`osiągnięcie "${a.id}": nieznana strefa`);
    if (c.type === 'rarity' && !rarities[c.rarity]) problems.push(`osiągnięcie "${a.id}": nieznana rzadkość "${c.rarity}"`);
  }
  if (Object.keys(banners).length === 0) problems.push('brak banerów przywołania');
  for (const b of Object.values(banners)) {
    for (const id of b.rotation.flat()) if (!items[id]) problems.push(`baner "${b.id}": nieznany przedmiot "${id}"`);
    if (b.featuredMinRarity && !rarities[b.featuredMinRarity]) problems.push(`baner "${b.id}": nieznana rzadkość "${b.featuredMinRarity}"`);
    if (b.shop && !rarities[b.shop.rarity]) problems.push(`baner "${b.id}": sklepik z nieznaną rzadkością "${b.shop.rarity}"`);
    if (b.shop && b.rotation.length === 0) problems.push(`baner "${b.id}": sklepik wymaga wyróżnionych przedmiotów`);
  }
  for (const id of Object.keys(game.gacha.shardsPerDuplicate)) if (!rarities[id]) problems.push(`gacha.shardsPerDuplicate: nieznana rzadkość "${id}"`);
  if (problems.length > 0) throw new ContentError(problems);
  return { game, classes, abilities, talents, enemies, items, rarities, rarityOrder: Object.keys(rarities), zones, upgrades, races, relics, achievements, banners };
}
